// The product page.
//
// A product's profile decides how it is presented (its page): a book or an
// audiobook reads on the book page, a music album on the same one, everything
// else — a physical product, a licence, a download — on the store page
// (contentRoutes.getContentRoute). The url prefix is how the product is
// addressed, not how it is rendered: /book, /album and /product all arrive
// here, the product is loaded once, and its profile picks the layout. A url whose
// prefix disagrees with the profile is answered with a 301 to the right one
// (productSSR), so each product keeps one address without the page ever
// rendering as the wrong thing.
import React, { useEffect, useRef, useState } from "react"
import { usePageContext } from "vike-react/usePageContext"
import Spinner from "../ui/Spinner"
import ProductUnavailable from "../ui/ProductUnavailable"
import BookPage from "./ProductPage"
import StorePage from "./StoreProductPage"
import useProduct from "../hooks/useProduct"
import useT from "../hooks/useT"
import { displayName, isProductOffered } from "../utils/productUtils"
import { getContentRoute } from "../utils/contentRoutes"
import { productOnBeforeRender } from "./productSSR"
import useClientAuth from "../hooks/useClientAuth"

/**
 * `?preview=1`: a product not offered yet (a draft, one waiting for review) is
 * shown as it will look, to whom may see it before the buyers do — an admin,
 * or a member of the company that sells it. Anyone else gets the notice.
 */
function usePreview(product) {
  const pageContext = usePageContext()
  const { auth, ready } = useClientAuth()
  const asked = /(?:^|[?&])preview=1(?:&|$)/.test(pageContext.urlParsed?.searchOriginal || (typeof window !== "undefined" ? window.location.search : ""))
  const [allowed, setAllowed] = useState(false)
  useEffect(() => {
    if (!asked || !ready || !auth?._id || !product) return
    let alive = true
    // loaded only when asked: the admin's code stays out of the buyers' pages
    import("../../AdminShared/vendor-role").then(({ resolveVendorAccess }) => resolveVendorAccess(auth._id)).then((access) => {
      if (!alive) return
      const seller = product.publisherSlug
      setAllowed(access.isAdmin || access.orgs.some((org) => seller && (org._id === seller || org.cid === seller)))
    })
    return () => {
      alive = false
    }
  }, [asked, ready, auth?._id, product?.pid])
  return asked && allowed
}

const PreviewBanner = () => (
  <div className="sticky top-0 z-30 bg-amber-100 text-amber-900 text-sm text-center px-4 py-2" data-testid="product-preview-banner">
    Preview: this product is not published, buyers do not see this page yet.
  </div>
)

export default function ProductRoute() {
  const t = useT()
  const { pid, sub, product, fresh, pending, refresh } = useProduct()
  // leaving the review flow (leave-review → thank-you → close) means the
  // product's ratings moved: fetch it again so the page shows them
  const subPath = (sub || []).join("/")
  const previousSub = useRef(subPath)
  useEffect(() => {
    if (previousSub.current.startsWith("leave-review") && !subPath) refresh()
    previousSub.current = subPath
  }, [subPath, refresh])

  const preview = usePreview(product)

  if (!pid) return null
  // a product that is gone (deleted, an old link) gets the same page as one
  // that is not offered: the way to what is available, not a bare notice
  if (!product) return pending ? <Spinner className="py-32" /> : <ProductUnavailable />
  // a product the store no longer offers has no page of its own: the whole
  // template becomes the notice, so nothing here can be read or bought
  const offered = isProductOffered(product)
  if (!offered && !preview) return <ProductUnavailable name={displayName(product)} />

  const Page = getContentRoute(product) === "product" ? StorePage : BookPage
  if (offered) return <Page pid={pid} sub={sub} product={product} ownershipKnown={fresh} />
  return (
    <>
      <PreviewBanner />
      <Page pid={pid} sub={sub} product={product} ownershipKnown={fresh} />
    </>
  )
}

ProductRoute.onBeforeRender = productOnBeforeRender
